import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean, jsonb, check, foreignKey } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { userProfiles } from "./schema-security.js";

// ============================================================================
// CRM MODULE TABLES (7 tables)
// ============================================================================

// 1. Client Groups Table (Top-level organizational container - CREATED FIRST)
export const clientGroups = pgTable("crm_x_client_groups", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  groupName: varchar("group_name", { length: 255 }).notNull(),
  description: text("description"),
  referringPartnerId: uuid("referring_partner_id"),
  // Sprint 27: Multi-tenancy columns
  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 2. Contacts Table (MUST belong to ONE client group)
export const contactTypeEnum = pgEnum("contact_type", ["individual", "business_contact"]);
export const leadSourceEnum = pgEnum("lead_source_type", [
  "website",
  "referral",
  "cold_call",
  "email_campaign",
  "social_media",
  "event",
  "partner",
  "existing_client",
  "other"
]);

export const contacts = pgTable("crm_x_contacts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  clientGroupId: uuid("client_group_id").references(() => clientGroups.id, { onDelete: "restrict" }).notNull(),
  contactType: contactTypeEnum("contact_type").default("individual"),
  firstName: varchar("first_name", { length: 100 }),
  middleName: varchar("middle_name", { length: 100 }),
  lastName: varchar("last_name", { length: 100 }),
  preferredName: varchar("preferred_name", { length: 100 }),
  email: varchar("email", { length: 255 }),
  mobile: varchar("mobile", { length: 20 }),
  isOverseasMobile: boolean("is_overseas_mobile").default(false),
  phone: varchar("phone", { length: 20 }),
  streetAddress: text("street_address"),
  suburb: varchar("suburb", { length: 100 }),
  state: varchar("state", { length: 50 }),
  postcode: varchar("postcode", { length: 10 }),
  leadSource: leadSourceEnum("lead_source"),
  tags: text("tags").array(),
  notes: text("notes"),
  linkedin: varchar("linkedin", { length: 500 }),
  partnerId: uuid("partner_id"),
  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  // Sprint 26 F001: ID Verification fields
  idVerificationStatus: varchar("id_verification_status", { length: 20 }).default("not_started"),
  idDocumentType: varchar("id_document_type", { length: 50 }),
  idDocumentNumber: varchar("id_document_number", { length: 100 }),
  idDocumentExpiry: date("id_document_expiry"),
  idVerifiedAt: timestamp("id_verified_at", { withTimezone: true }),
  idVerifiedBy: uuid("id_verified_by"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
  archivedAt: timestamp("archived_at", { withTimezone: true }),
  archivedByProfileId: uuid("archived_by_profile_id"),
}, (table) => [
  check("crm_x_contacts_archive_pair_check", sql`(${table.archivedAt} IS NULL) = (${table.archivedByProfileId} IS NULL)`),
  foreignKey({
    name: "crm_x_contacts_archive_author_fk",
    columns: [table.archivedByProfileId],
    foreignColumns: [userProfiles.id],
  }).onDelete("no action"),
]);

// 3. Accounts Table (Business entities - MUST belong to ONE client group)
export const entityTypeEnum = pgEnum("entity_type", ["company", "trust", "partnership", "sole_trader"]);
export const companyClassEnum = pgEnum("company_class", ["proprietary_limited", "public_limited", "foreign_company"]);
export const partnershipTypeEnum = pgEnum("partnership_type", ["general", "limited"]);
export const smsfComplyingStatusEnum = pgEnum("smsf_complying_status", ["complying", "non_complying", "pending"]);
export const industryEnum = pgEnum("industry_type", [
  "agriculture_forestry_fishing",
  "mining",
  "manufacturing",
  "construction",
  "retail_trade",
  "wholesale_trade",
  "accommodation_food_services",
  "transport_postal_warehousing",
  "information_media_telecommunications",
  "financial_insurance_services",
  "asset_holding",
  "rental_hiring_real_estate_services",
  "professional_scientific_technical_services",
  "administrative_support_services",
  "public_administration_safety",
  "education_training",
  "health_care_social_assistance",
  "arts_recreation_services",
  "electricity_gas_water_waste",
  "other_services"
]);

export const accounts = pgTable("crm_x_accounts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  clientGroupId: uuid("client_group_id").references(() => clientGroups.id, { onDelete: "restrict" }).notNull(),
  entityType: entityTypeEnum("entity_type").notNull(),
  legalName: varchar("legal_name", { length: 255 }).notNull(),
  tradingName: varchar("trading_name", { length: 255 }),

  // Common entity identifiers (shared across multiple types)
  abn: varchar("abn", { length: 11 }),
  abnRegistrationDate: date("abn_registration_date"),
  gstRegistrationDate: date("gst_registration_date"),

  // Company-specific fields
  acn: varchar("acn", { length: 9 }),
  acnStartDate: date("acn_start_date"),
  companyClass: companyClassEnum("company_class"), // ASIC company class (company entities only)

  // Trust-specific fields
  trustType: varchar("trust_type", { length: 50 }), // "family", "unit", "discretionary"
  trusteeType: varchar("trustee_type", { length: 50 }), // "individual", "corporate"
  trustDeedDate: date("trust_deed_date"),

  // SMSF-specific fields (trust_type = 'smsf' or 'bare_trust')
  smsfComplyingStatus: smsfComplyingStatusEnum("smsf_complying_status"), // ATO compliance status
  smsfAtoCheckedAt: timestamp("smsf_ato_checked_at"), // Date of ATO verification
  linkedSmsfId: uuid("linked_smsf_id"), // For bare trusts: FK to SMSF account

  // Partnership-specific fields
  partnershipType: partnershipTypeEnum("partnership_type"), // general, limited

  // Common business fields
  industry: industryEnum("industry"),
  annualRevenue: numeric("annual_revenue", { precision: 15, scale: 2 }),
  employeeCount: integer("employee_count"),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  streetAddress: text("street_address"),
  suburb: varchar("suburb", { length: 100 }),
  state: varchar("state", { length: 50 }),
  postcode: varchar("postcode", { length: 10 }),
  notes: text("notes"),
  partnerId: uuid("partner_id"),

  // Online presence fields
  website: varchar("website", { length: 500 }),
  linkedin: varchar("linkedin", { length: 500 }),

  // Industry classification
  anzsicCode: varchar("anzsic_code", { length: 6 }),

  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
  archivedAt: timestamp("archived_at", { withTimezone: true }),
  archivedByProfileId: uuid("archived_by_profile_id"),
}, (table) => [
  check("crm_x_accounts_archive_pair_check", sql`(${table.archivedAt} IS NULL) = (${table.archivedByProfileId} IS NULL)`),
  foreignKey({
    name: "crm_x_accounts_archive_author_fk",
    columns: [table.archivedByProfileId],
    foreignColumns: [userProfiles.id],
  }).onDelete("no action"),
]);

// 4. Account-Contact Relationships Table (Many-to-many with relationship types)
export const accountContacts = pgTable("crm_x_account_contacts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  relationshipType: varchar("relationship_type", { length: 50 }), // LEGACY: Single relationship type (use accountContactRoles instead)
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }),
  appointedDate: date("appointed_date"),
  ceasedDate: date("ceased_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// 4-roles. Account Contact Roles Junction Table (F009 - Multi-select relationship types)
export const accountContactRoles = pgTable("crm_x_account_contact_roles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountContactId: uuid("account_contact_id").references(() => accountContacts.id, { onDelete: "cascade" }).notNull(),
  roleType: varchar("role_type", { length: 50 }).notNull(), // director, shareholder, beneficial_owner, beneficiary, secretary, trustee, etc.
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }), // For shareholder role only
  isBeneficialOwner: boolean("is_beneficial_owner").default(false), // For beneficiary auto-link
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4a. Account People Table (Directors, Beneficial Owners, Individual Trustees)
export const accountPeopleRoleEnum = pgEnum("account_people_role", ["director", "beneficial_owner", "trustee", "proprietor"]);

export const accountPeople = pgTable("crm_x_account_people", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  role: accountPeopleRoleEnum("role").notNull(),
  organisationId: uuid("organisation_id"),
  appointmentDate: date("appointment_date"),
  ceasedDate: date("ceased_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4b. Shareholders Table (Share ownership tracking)
export const shareClassEnum = pgEnum("share_class", [
  "ordinary",
  "class_a",
  "class_b",
  "class_c",
  "class_d",
  "class_e",
  "preference",
  "redeemable_preference",
  "performance",
  "employee"
]);

export const shareholders = pgTable("crm_x_shareholders", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  // REGRESSION-FIX-1 F005 AC003: contactId is now nullable — either contactId OR shareholderAccountId must be set
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  // AC003: Account as shareholder (corporate shareholder support)
  shareholderAccountId: uuid("shareholder_account_id").references(() => accounts.id, { onDelete: "cascade" }),
  organisationId: uuid("organisation_id"),
  shareClass: shareClassEnum("share_class").default("ordinary").notNull(),
  numberOfShares: numeric("number_of_shares", { precision: 15, scale: 0 }),
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }),
  appointmentDate: date("appointment_date"),
  ceasedDate: date("ceased_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4c. Trustee Companies Table (Corporate trustees for trusts)
export const trusteeCompanies = pgTable("crm_x_trustee_companies", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  trustAccountId: uuid("trust_account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  trusteeAccountId: uuid("trustee_account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  organisationId: uuid("organisation_id"),
  appointmentDate: date("appointment_date"),
  ceasedDate: date("ceased_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4d. Account Beneficial Owners Table (UBO tracking for AML/CTF compliance)
// REMOVED (ISSUE-051): Legacy crm_x_account_beneficial_owners table
// Beneficial owners now tracked via crm_x_account_people with role='beneficial_owner'
// Per PRD v1.3 Breaking Change: Contact linking REQUIRED

// 4e. Contact Employment Table (Employment history with income tracking)
export const employmentStatusEnum = pgEnum("employment_status", [
  "full_time", "part_time", "casual", "contract", "self_employed",
  "unemployed", "retired", "student", "home_duties"
]);
export const salaryFrequencyEnum = pgEnum("salary_frequency", [
  "weekly", "fortnightly", "monthly", "quarterly", "annually"
]);

export const contactEmployment = pgTable("crm_x_contact_employment", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  employmentStatus: employmentStatusEnum("employment_status"),
  isCurrent: integer("is_current").default(0).notNull(),
  employerName: varchar("employer_name", { length: 255 }),
  employerAbn: varchar("employer_abn", { length: 11 }),
  employerIndustry: varchar("employer_industry", { length: 100 }),
  employerPhone: varchar("employer_phone", { length: 20 }),
  jobTitle: varchar("job_title", { length: 255 }),
  department: varchar("department", { length: 255 }),
  responsibilities: text("responsibilities"),
  startDate: date("start_date"),
  endDate: date("end_date"),
  yearsInIndustry: numeric("years_in_industry", { precision: 4, scale: 2 }),
  baseSalary: numeric("base_salary", { precision: 15, scale: 2 }),
  salaryFrequency: salaryFrequencyEnum("salary_frequency"),
  isVerified: integer("is_verified").default(0).notNull(),
  verificationMethod: varchar("verification_method", { length: 100 }),
  verifiedAt: timestamp("verified_at"),
  onProbation: integer("on_probation").default(0).notNull(),
  probationEndDate: date("probation_end_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4f. Trust Beneficiaries Table (Named beneficiaries for unit trusts)
// SPRINT-2 Gap-Fill: Contact linking required per BR-BEN-001, beneficiaryName removed
// SPRINT-26 F003: Added isBeneficialOwner + linkedBeneficialOwnerId for UBO toggle (BR-BEN-004)
export const trustBeneficiaries = pgTable("crm_x_trust_beneficiaries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  trustAccountId: uuid("trust_account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  beneficiaryContactId: uuid("beneficiary_contact_id").references(() => contacts.id, { onDelete: "restrict" }).notNull(), // REQUIRED per BR-BEN-001
  beneficiaryAccountId: uuid("beneficiary_account_id").references(() => accounts.id, { onDelete: "set null" }),
  organisationId: uuid("organisation_id"),
  // beneficiaryName REMOVED (SPRINT-2 Gap-Fill) - name derived from linked contact per PRD v1.3
  beneficiaryClass: varchar("beneficiary_class", { length: 50 }), // 'primary', 'secondary', 'contingent', 'default'
  unitCount: integer("unit_count"),
  unitPercentage: numeric("unit_percentage", { precision: 5, scale: 2 }),
  entitlementType: varchar("entitlement_type", { length: 30 }).default("income_and_capital"), // 'income_only', 'capital_only', 'income_and_capital'
  isActive: integer("is_active").default(1).notNull(),
  effectiveFrom: date("effective_from"),
  effectiveTo: date("effective_to"),
  // SPRINT-26 F003: UBO toggle fields (BR-BEN-004)
  isBeneficialOwner: integer("is_beneficial_owner").default(0).notNull(),
  linkedBeneficialOwnerId: uuid("linked_beneficial_owner_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 4g. SMSF Members Table (SMSF member details with balance tracking)
export const smsfMembers = pgTable("crm_x_smsf_members", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  smsfAccountId: uuid("smsf_account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id).notNull(),
  memberSince: date("member_since"),
  isActive: integer("is_active").default(1).notNull(),
  accumulationBalance: numeric("accumulation_balance", { precision: 15, scale: 2 }).default("0"),
  pensionBalance: numeric("pension_balance", { precision: 15, scale: 2 }).default("0"),
  concessionalContributionsYtd: numeric("concessional_contributions_ytd", { precision: 15, scale: 2 }).default("0"),
  nonConcessionalContributionsYtd: numeric("non_concessional_contributions_ytd", { precision: 15, scale: 2 }).default("0"),
  isInPensionPhase: integer("is_in_pension_phase").default(0).notNull(),
  pensionStartDate: date("pension_start_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// CRM MODULE ZOD SCHEMAS
// ============================================================================

// CRM Module Schemas
export const insertClientGroupSchema = createInsertSchema(clientGroups).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true
});
export const updateClientGroupSchema = insertClientGroupSchema.partial(); // createdBy already omitted
export const selectClientGroupSchema = createSelectSchema(clientGroups);

export const insertContactSchema = createInsertSchema(contacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  archivedByProfileId: true,
  createdBy: true
}).extend({
  idVerificationStatus: z.enum(['not_started', 'pending', 'verified', 'failed', 'expired']).nullable().optional(),
  idDocumentType: z.enum(['drivers_licence', 'passport', 'medicare_card', 'proof_of_age', 'immicard', 'other']).nullable().optional(),
  idDocumentNumber: z.string().max(100).nullable().optional(),
  idDocumentExpiry: z.string().nullable().optional(),
});
export const updateContactSchema = insertContactSchema.partial(); // createdBy already omitted
export const selectContactSchema = createSelectSchema(contacts);

const baseAccountSchema = createInsertSchema(accounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  archivedByProfileId: true,
  createdBy: true
}).extend({
  // ISSUE-573: Strip whitespace from ABN/ACN before length validation.
  // createInsertSchema auto-generates z.string().max(11) from varchar(11), which rejects
  // pasted values like "53 004 085 616" (14 chars with spaces). We override with a
  // preprocess transform that strips all whitespace before Zod validates.
  abn: z.preprocess(
    (val) => typeof val === "string" ? val.replace(/\s/g, "") : val,
    z.string().max(11).nullable().optional()
  ) as unknown as z.ZodOptional<z.ZodNullable<z.ZodString>>,
  acn: z.preprocess(
    (val) => typeof val === "string" ? val.replace(/\s/g, "") : val,
    z.string().max(9).nullable().optional()
  ) as unknown as z.ZodOptional<z.ZodNullable<z.ZodString>>,
  // ISSUE-168 Rework #2: Handle smsfAtoCheckedAt timestamp - accept string and convert to Date
  // CRITICAL: z.string() must come BEFORE z.date() in union because:
  //   - JSON data from API always arrives as string (ISO format)
  //   - z.date() expects Date object, rejects strings with "Expected date, received string"
  //   - By checking z.string() first, we can transform it to Date before z.date() is attempted
  smsfAtoCheckedAt: z.union([
    z.string().nullable().transform(val => val ? new Date(val) : null),
    z.date(),
    z.null()
  ]).optional(),
});

export const insertAccountSchema = baseAccountSchema
  .refine((data) => {
    if (data.abn && data.abn.trim() !== "") {
      const abnDigits = data.abn.replace(/\s/g, "");
      if (!/^\d+$/.test(abnDigits) || abnDigits.length !== 11) {
        return false;
      }
    }
    return true;
  }, {
    message: "ABN must be exactly 11 digits",
    path: ["abn"],
  })
  .refine((data) => {
    if (data.acn && data.acn.trim() !== "") {
      const acnDigits = data.acn.replace(/\s/g, "");
      if (!/^\d+$/.test(acnDigits) || acnDigits.length !== 9) {
        return false;
      }
    }
    return true;
  }, {
    message: "ACN must be exactly 9 digits",
    path: ["acn"],
  })
;

export const updateAccountSchema = baseAccountSchema
  .partial()
  .refine((data) => {
    if (data.abn && data.abn.trim() !== "") {
      const abnDigits = data.abn.replace(/\s/g, "");
      if (!/^\d+$/.test(abnDigits) || abnDigits.length !== 11) {
        return false;
      }
    }
    return true;
  }, {
    message: "ABN must be exactly 11 digits",
    path: ["abn"],
  })
  .refine((data) => {
    if (data.acn && data.acn.trim() !== "") {
      const acnDigits = data.acn.replace(/\s/g, "");
      if (!/^\d+$/.test(acnDigits) || acnDigits.length !== 9) {
        return false;
      }
    }
    return true;
  }, {
    message: "ACN must be exactly 9 digits",
    path: ["acn"],
  })
; // createdBy already omitted via baseAccountSchema
export const selectAccountSchema = createSelectSchema(accounts);

export const insertAccountContactSchema = createInsertSchema(accountContacts).omit({
  id: true,
  createdAt: true
});
export const updateAccountContactSchema = insertAccountContactSchema.partial(); // No createdBy field
export const selectAccountContactSchema = createSelectSchema(accountContacts);

// F009 - Account Contact Roles Junction Table Schemas
export const insertAccountContactRoleSchema = createInsertSchema(accountContactRoles).omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  // Validation: ownershipPercentage required only for 'shareholder' role
  ownershipPercentage: z.string().nullable().optional(),
  isBeneficialOwner: z.boolean().default(false).optional(),
});
export const updateAccountContactRoleSchema = insertAccountContactRoleSchema.partial();
export const selectAccountContactRoleSchema = createSelectSchema(accountContactRoles);

export const insertAccountPeopleSchema = createInsertSchema(accountPeople).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
// EDG-6537: accountId omitted so PATCH bodies cannot re-parent rows across accounts
export const updateAccountPeopleSchema = insertAccountPeopleSchema.partial().omit({ organisationId: true, accountId: true });
export const selectAccountPeopleSchema = createSelectSchema(accountPeople);

export const insertShareholderSchema = createInsertSchema(shareholders).omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  // AC003: contactId is now optional (either contactId or shareholderAccountId must be set)
  contactId: z.string().uuid().nullable().optional(),
  shareholderAccountId: z.string().uuid().nullable().optional(),
  numberOfShares: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  ownershipPercentage: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
}).refine(
  (data) => (data.contactId != null) !== (data.shareholderAccountId != null),
  { message: "Exactly one of contactId or shareholderAccountId must be provided" }
);
export const updateShareholderSchema = createInsertSchema(shareholders).omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  contactId: z.string().uuid().nullable().optional(),
  shareholderAccountId: z.string().uuid().nullable().optional(),
  numberOfShares: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  ownershipPercentage: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
}).partial().omit({ organisationId: true, accountId: true }); // EDG-6537: accountId omitted so PATCH bodies cannot re-parent rows
export const selectShareholderSchema = createSelectSchema(shareholders);

export const insertTrusteeCompanySchema = createInsertSchema(trusteeCompanies).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
// EDG-6537: trustAccountId omitted so PATCH bodies cannot re-parent rows across trusts
export const updateTrusteeCompanySchema = insertTrusteeCompanySchema.partial().omit({ organisationId: true, trustAccountId: true });
export const selectTrusteeCompanySchema = createSelectSchema(trusteeCompanies);

// Contact Employment Schemas
export const insertContactEmploymentSchema = createInsertSchema(contactEmployment).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  verifiedAt: true
}).extend({
  // ISSUE-573: Strip whitespace from employer ABN before length validation (same fix as accounts.abn)
  employerAbn: z.preprocess(
    (val) => typeof val === "string" ? val.replace(/\s/g, "") : val,
    z.string().max(11).nullable().optional()
  ) as unknown as z.ZodOptional<z.ZodNullable<z.ZodString>>,
  yearsInIndustry: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  baseSalary: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
});
export const updateContactEmploymentSchema = insertContactEmploymentSchema.partial();
export const selectContactEmploymentSchema = createSelectSchema(contactEmployment);

// Trust Beneficiaries Schemas
export const insertTrustBeneficiarySchema = createInsertSchema(trustBeneficiaries).omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  unitPercentage: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
});
// EDG-6537: trustAccountId omitted so PATCH bodies cannot re-parent rows across trusts
export const updateTrustBeneficiarySchema = insertTrustBeneficiarySchema.partial().omit({ organisationId: true, trustAccountId: true });
export const selectTrustBeneficiarySchema = createSelectSchema(trustBeneficiaries);

// SMSF Members Schemas
export const insertSmsfMemberSchema = createInsertSchema(smsfMembers).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateSmsfMemberSchema = insertSmsfMemberSchema.partial();
export const selectSmsfMemberSchema = createSelectSchema(smsfMembers);

// ============================================================================
// CRM MODULE TYPES
// ============================================================================

// CRM Module Types
export type InsertClientGroup = z.infer<typeof insertClientGroupSchema>;
export type UpdateClientGroup = z.infer<typeof updateClientGroupSchema>;
export type ClientGroup = typeof clientGroups.$inferSelect;

export type InsertContact = z.infer<typeof insertContactSchema>;
export type UpdateContact = z.infer<typeof updateContactSchema>;
export type Contact = typeof contacts.$inferSelect;

export type InsertAccount = z.infer<typeof insertAccountSchema>;
export type UpdateAccount = z.infer<typeof updateAccountSchema>;
export type Account = typeof accounts.$inferSelect;

export type InsertAccountContact = z.infer<typeof insertAccountContactSchema>;
export type UpdateAccountContact = z.infer<typeof updateAccountContactSchema>;
export type AccountContact = typeof accountContacts.$inferSelect;

export type InsertAccountContactRole = z.infer<typeof insertAccountContactRoleSchema>;
export type UpdateAccountContactRole = z.infer<typeof updateAccountContactRoleSchema>;
export type AccountContactRole = typeof accountContactRoles.$inferSelect;

export type InsertAccountPeople = z.infer<typeof insertAccountPeopleSchema>;
export type UpdateAccountPeople = z.infer<typeof updateAccountPeopleSchema>;
export type AccountPeople = typeof accountPeople.$inferSelect;

export type InsertShareholder = z.infer<typeof insertShareholderSchema>;
export type UpdateShareholder = z.infer<typeof updateShareholderSchema>;
export type Shareholder = typeof shareholders.$inferSelect;

export type InsertTrusteeCompany = z.infer<typeof insertTrusteeCompanySchema>;
export type UpdateTrusteeCompany = z.infer<typeof updateTrusteeCompanySchema>;
export type TrusteeCompany = typeof trusteeCompanies.$inferSelect;

// Contact Employment Types
export type InsertContactEmployment = z.infer<typeof insertContactEmploymentSchema>;
export type UpdateContactEmployment = z.infer<typeof updateContactEmploymentSchema>;
export type ContactEmployment = typeof contactEmployment.$inferSelect;

// Trust Beneficiary Types
export type InsertTrustBeneficiary = z.infer<typeof insertTrustBeneficiarySchema>;
export type UpdateTrustBeneficiary = z.infer<typeof updateTrustBeneficiarySchema>;
export type TrustBeneficiary = typeof trustBeneficiaries.$inferSelect;

// SMSF Member Types
export type InsertSmsfMember = z.infer<typeof insertSmsfMemberSchema>;
export type UpdateSmsfMember = z.infer<typeof updateSmsfMemberSchema>;
export type SmsfMember = typeof smsfMembers.$inferSelect;

// ============================================================================
// DUPLICATE DETECTION MODULE (Sprint 28 F006)
// ============================================================================

export const duplicateMatchStatusEnum = pgEnum("duplicate_match_status", [
  "unresolved", "merged", "dismissed", "kept_both"
]);

export const duplicateMatches = pgTable("crm_x_duplicate_matches", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: varchar("entity_type", { length: 20 }).notNull(),
  sourceId: uuid("source_id").notNull(),
  targetId: uuid("target_id").notNull(),
  matchScore: numeric("match_score", { precision: 3, scale: 2 }).notNull(),
  matchFields: jsonb("match_fields").notNull().default(sql`'[]'::jsonb`),
  matchReason: text("match_reason"),
  status: varchar("status", { length: 20 }).notNull().default("unresolved"),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  resolvedBy: uuid("resolved_by"),
  resolutionNotes: text("resolution_notes"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// Duplicate Matches Schemas
export const insertDuplicateMatchSchema = createInsertSchema(duplicateMatches).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resolvedAt: true,
  resolvedBy: true,
});
export const updateDuplicateMatchSchema = insertDuplicateMatchSchema.partial();
export const selectDuplicateMatchSchema = createSelectSchema(duplicateMatches);

// Duplicate Matches Types
export type InsertDuplicateMatch = z.infer<typeof insertDuplicateMatchSchema>;
export type UpdateDuplicateMatch = z.infer<typeof updateDuplicateMatchSchema>;
export type DuplicateMatch = typeof duplicateMatches.$inferSelect;
